import { Box, Card as MuiCard, CardActions, SxProps, Theme } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

import { CARD_MAX_WIDTH, HOVER_MEDIA } from '../../../../constants/ui';

// Corner badge and fill-the-frame media shared by the single- and multi-item tiles.
export const cornerChipSx: SxProps<Theme> = { position: 'absolute', top: 1, right: 1, zIndex: 2 };

export const fillMediaSx: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'contain',
};

// Flex column, not CSS subgrid: the flex carousel has no grid rows for subgrid to bind to.
export const StyledCard = styled(MuiCard)(({ theme: { palette, transitions } }) => ({
  position: 'relative',
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: CARD_MAX_WIDTH,
  height: '100%',
  padding: 16,
  backgroundColor: palette.background.paper,
  border: `1px solid ${palette.divider}`,
  borderRadius: 4,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  // Selected ring overlays the border at the same radius so toggling it causes no layout shift.
  '&.is-selected': {
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: -1,
      border: `2px solid ${palette.primary.main}`,
      borderRadius: 4,
      pointerEvents: 'none',
    },
  },
  [HOVER_MEDIA]: {
    '&:hover:not(.is-disabled)::after': {
      opacity: 1,
    },
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: 4,
    backgroundColor: palette.action.hover,
    opacity: 0,
    transition: transitions.create(['opacity'], { duration: transitions.duration.shorter }),
    pointerEvents: 'none',
    zIndex: 1,
  },
}));

export const StyledContent = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: 0,
  flex: '0 0 auto',
  minHeight: 0,
}));

export const HeaderRow = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 8,
  width: '100%',
}));

export const TitleColumn = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 0,
}));

export const CardTitle = styled(Box)(({ theme: { palette } }) => ({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  minWidth: 0,
  fontWeight: 500,
  fontSize: 16,
  lineHeight: 1.4,
  minHeight: '1.4em',
  color: palette.text.primary,
}));

export const SingleItemContainer = styled(Box)(() => ({
  position: 'relative',
  width: '100%',
  aspectRatio: '1 / 1',
  borderRadius: 4,
  overflow: 'hidden',
}));

export const SingleItemMedia = styled(Box)(() => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const MultipleItemsContainer = styled(Box)(() => ({
  width: '100%',
  aspectRatio: '1 / 1',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gridTemplateRows: 'auto auto',
  gap: 8,
}));

export const SkuTile = styled(Box)(({ theme: { palette } }) => ({
  position: 'relative',
  minWidth: 0,
  minHeight: 0,
  aspectRatio: '1 / 1',
  boxSizing: 'border-box',
  border: `1px solid ${palette.divider}`,
  borderRadius: 4,
  padding: '16px 8px 8px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 4,
  overflow: 'hidden',
}));

export const SkuTileMedia = styled(Box)(() => ({
  position: 'relative',
  flex: '1 1 auto',
  alignSelf: 'stretch',
  minHeight: 0,
  width: '100%',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const SkuTileName = styled(Box)(({ theme: { palette } }) => ({
  fontWeight: 500,
  fontSize: 12,
  lineHeight: '20px',
  color: palette.text.secondary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  textAlign: 'left',
  alignSelf: 'stretch',
  minWidth: 0,
}));

export const OverflowTile = styled(Box)(({ theme: { palette } }) => ({
  minHeight: 0,
  aspectRatio: '1 / 1',
  border: `1px solid ${palette.divider}`,
  borderRadius: 4,
  backgroundColor: alpha(palette.primary.main, palette.action.hoverOpacity),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 400,
  fontSize: 20,
  color: palette.primary.main,
  cursor: 'pointer',
}));

export const StyledActions = styled(CardActions)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  gap: 8,
  padding: 0,
  // Bottom-pinned so Select lands on the same line across cards whose media differ in height.
  marginTop: 'auto',
}));
